import { faBarsFilter } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ZonedDateTime } from '@js-joda/core';
import isEqual from 'lodash/isEqual';
import React from 'react';
import styled from 'styled-components';
import { DateRange } from '../../../graphql/types';
import useTracking from '../../../hooks/useTracking';
import {
  getLocalTimeUtcEndOfDay,
  getLocalTimeUtcStartOfDay,
} from '../../../utils/date';
import {
  EventCategories,
  EventLabels,
  EventNames,
  EventProfileCardNames,
  EventSubCategories,
} from '../../../utils/trackingEvents';
import Button, { ButtonIconPosition } from '../Button';
import { DateRangePicker, ZdtDateRange } from '../DateRangePicker';
import { RangeTypeWithEventLabel } from '../DateRangePicker/predefinedRanges';

const WINDOW_EVENT_LABELS: Record<number, EventLabels> = {
  45: EventLabels.Next45days,
  90: EventLabels.Next90days,
};

const StyledHeader = styled.header`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
`;

const LeftContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

export type DateRangeFilterState = [
  queryParams: { dateRange: DateRange },
  setQueryParams: (updates: { dateRange: DateRange }) => void,
];

interface FiltersHeaderProps {
  filters: DateRangeFilterState;
  eventCategory: EventCategories;
  defaultDateRange: DateRange;
  /** How far ahead the schedule can be asked about. */
  windowDays?: number;
  /** Shown in the picker's footer */
  note?: string;
  isExpanded?: boolean;
  toggleShowSidebar?: () => void;
}
export const FiltersHeader: React.FC<FiltersHeaderProps> = ({
  filters: [queryVariables, setQueryVariables],
  eventCategory,
  defaultDateRange,
  windowDays = 45,
  note,
  isExpanded,
  toggleShowSidebar,
}) => {
  const { dateRange } = queryVariables;

  const { track } = useTracking();

  const startOfToday = getLocalTimeUtcStartOfDay();
  const endOfToday = getLocalTimeUtcEndOfDay();
  const zdtToJsDate = (zdt: ZonedDateTime) =>
    new Date(zdt.withFixedOffsetZone().toJSON());

  const customPredefinedRanges: RangeTypeWithEventLabel[] = [
    {
      eventlabel: EventLabels.Today,
      label: 'Today',
      value: [zdtToJsDate(startOfToday), zdtToJsDate(endOfToday)],
      placement: 'left',
    },
    {
      eventlabel: EventLabels.Next7days,
      label: 'Next 7 Days',
      value: [zdtToJsDate(startOfToday), zdtToJsDate(endOfToday.plusDays(7))],
      placement: 'left',
    },
    {
      eventlabel: EventLabels.Next30days,
      label: 'Next 30 Days',
      value: [zdtToJsDate(startOfToday), zdtToJsDate(endOfToday.plusDays(30))],
      placement: 'left',
    },
    {
      eventlabel: WINDOW_EVENT_LABELS[windowDays] ?? EventLabels.Next45days,
      label: `Next ${windowDays} Days`,
      value: [
        zdtToJsDate(startOfToday),
        zdtToJsDate(endOfToday.plusDays(windowDays)),
      ],
      placement: 'left',
    },
  ];

  // Today as the start limit, the end of the window as the end limit.
  const shouldDisableDate = (date: Date): boolean => {
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Set to start of today

    const endOfToday = new Date(today);
    endOfToday.setHours(23, 59, 59, 999);

    const maxDate = new Date(endOfToday);
    maxDate.setDate(endOfToday.getDate() + windowDays);

    return date < today || date > maxDate;
  };
  const handleFilterButtonClick = () => {
    const nextStateIsExpanded = !isExpanded;
    track(EventNames.ButtonClick, {
      event_category: eventCategory,
      event_label: EventLabels.Filter,
      event_subcategory: nextStateIsExpanded
        ? EventSubCategories.Expand
        : EventSubCategories.Collapse,
    });
    toggleShowSidebar?.();
  };

  const handleDateRangeChange = (
    value: ZdtDateRange,
    buttonLabel?: EventLabels,
  ) => {
    track(EventNames.ButtonClick, {
      card_name: EventProfileCardNames.DateRangeModal,
      event_category: eventCategory,
      event_label: buttonLabel,
    });

    setQueryVariables({
      dateRange: {
        start: value.start?.toJSON(),
        end: value.end?.toJSON(),
      },
    });
  };

  const handleDateRangeClear = () => {
    track(EventNames.ButtonClick, {
      card_name: EventProfileCardNames.DateRangeModal,
      event_category: eventCategory,
      event_label: EventLabels.Reset,
    });
    if (dateRange.start || dateRange.end) {
      setQueryVariables({ dateRange: {} });
    }
  };

  const handleDateRangeOpen = () => {
    track(EventNames.CalendarIconClick, {
      event_category: eventCategory,
    });
  };

  return (
    <StyledHeader>
      <LeftContainer>
        {toggleShowSidebar && (
          <Button
            iconPosition={ButtonIconPosition.Left}
            testId="Button-FilterToggle"
            icon={<FontAwesomeIcon icon={faBarsFilter} />}
            onClick={handleFilterButtonClick}
          >
            Filter
          </Button>
        )}
        <DateRangePicker
          value={{
            start: dateRange.start
              ? ZonedDateTime.parse(dateRange.start)
              : null,
            end: dateRange.end ? ZonedDateTime.parse(dateRange.end) : null,
          }}
          clearable={
            !isEqual(dateRange, defaultDateRange) &&
            (Boolean(dateRange.start) || Boolean(dateRange.end))
          }
          onClear={handleDateRangeClear}
          onChange={handleDateRangeChange}
          onOpen={handleDateRangeOpen}
          shouldDisableDate={shouldDisableDate}
          predefinedRanges={customPredefinedRanges}
          note={note}
        />
      </LeftContainer>
    </StyledHeader>
  );
};
